import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, SafeAreaView, StatusBar, ScrollView, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { AppIcon, type AppIconName } from '../components/icons/AppIcon';
import { Button, DualPaneLayout, colors, spacing, typography } from '../components';
import { useAppContext, Screen } from '../contexts/AppContext';
import { useAppStore } from '../store/appStore';
import { useProfileCompletion } from '../hooks/useProfileCompletion';
import { useSetupProgress, SETUP_SECTIONS, SetupItem } from '../hooks/useSetupProgress';
import { useLocale } from '../contexts/LocaleContext';
import { useTypography } from '../hooks/useTypography';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useTheme } from '../contexts/ThemeContext';
import { withAlpha } from '../theme';

interface ChannelItem {
  serviceCode: string;
  titleKey: 'auth.channelWhatsApp' | 'auth.channelFacebookMessenger' | 'auth.channelXTwitter' | 'auth.channelPhone' | 'ai.setupTitle';
  descriptionKey: 'auth.channelWhatsAppDescription' | 'auth.channelFacebookMessengerDescription' | 'auth.channelXTwitterDescription' | 'auth.channelPhoneDescription' | 'ai.setupDescription';
  icon: AppIconName;
  color: string;
  screen: Screen;
}

const CHANNELS: ChannelItem[] = [
  { serviceCode: 'ai', titleKey: 'ai.setupTitle', descriptionKey: 'ai.setupDescription', icon: 'ai-bot', color: '#8B5CF6', screen: 'ai-setup' },
  { serviceCode: 'wabu', titleKey: 'auth.channelWhatsApp', descriptionKey: 'auth.channelWhatsAppDescription', icon: 'logo-whatsapp', color: '#25D366', screen: 'whatsapp-business-setup' },
  { serviceCode: 'fbm', titleKey: 'auth.channelFacebookMessenger', descriptionKey: 'auth.channelFacebookMessengerDescription', icon: 'logo-facebook', color: '#1877F2', screen: 'facebook-messenger-setup' },
  { serviceCode: 'x_twitter', titleKey: 'auth.channelXTwitter', descriptionKey: 'auth.channelXTwitterDescription', icon: 'logo-twitter', color: '#1D9BF0', screen: 'x-twitter-setup' },
  { serviceCode: 'phone', titleKey: 'auth.channelPhone', descriptionKey: 'auth.channelPhoneDescription', icon: 'call', color: '#0EA5E9', screen: 'phone-setup' },
];

/**
 * Last onboarding step, and "Get your business ready" from Home: the plan's
 * channels, then the first steps of each part of the business (contacts,
 * catalogue, team, sales) for owners/admins. Each row opens its own screen;
 * the list re-reads when this screen is shown again.
 */
export const ChannelOnboardingStepper: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo } = useAppContext();
  const { currentSubscription } = useAppStore();
  const { profileCompletion, isLoading } = useProfileCompletion();
  const setup = useSetupProgress('channel-onboarding-stepper');
  const { t, isRTL } = useLocale();
  const typo = useTypography();
  const { themeMode } = useTheme();

  // Plan service keys may be stored in either case (services are coded
  // uppercase, plans historically lowercase).
  const planServiceCodes = new Set(
    Object.keys((currentSubscription?.services || {}) as Record<string, unknown>).map((k) => k.toLowerCase())
  );
  const inPlan = CHANNELS.filter((c) => planServiceCodes.has(c.serviceCode));
  const channels = inPlan.length > 0 ? inPlan : CHANNELS.filter(c => c.serviceCode !== 'ai');
  const connected = new Set(
    profileCompletion.services.filter((s) => s.status === 'completed').map((s) => String(s.code).toLowerCase())
  );
  const connectedCount = channels.filter((c) => connected.has(c.serviceCode)).length;

  const openChannel = (channel: ChannelItem) =>
    navigateTo(channel.screen, undefined, { onCompleteScreen: 'channel-onboarding-stepper', onCompleteParams: {} });
  const openStep = (item: SetupItem) =>
    navigateTo(item.screen, undefined, { ...(item.routeParams || {}), onCompleteScreen: 'channel-onboarding-stepper', onCompleteParams: {} });

  // Channels are listed one by one above; the rest by section.
  const businessSections = SETUP_SECTIONS.filter((section) => section !== 'channels')
    .map((section) => ({ section, items: setup.items.filter((i) => i.section === section) }))
    .filter((group) => group.items.length > 0);
  const totalDone = setup.canSetUp ? setup.doneCount : connectedCount;
  const totalSteps = setup.canSetUp ? setup.total : channels.length;
  const progressRatio = totalSteps > 0 ? totalDone / totalSteps : 0;

  const renderStatus = (done: boolean, doneLabel: string, actionLabel: string) => (done ? (
    <View style={[styles.statusPill, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
      <Ionicons name="checkmark-circle" size={16} color={colors.status.success as string} />
      <Text style={styles.statusText}>{doneLabel}</Text>
    </View>
  ) : (
    <Text style={styles.connectText}>{actionLabel}</Text>
  ));

  return (
    <DualPaneLayout title="DISLIX" subtitle={t('auth.setUpChannels')}>
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle={themeMode === 'dark' ? 'light-content' : 'dark-content'} backgroundColor={colors.background.surface as string} />
        <ScrollView contentContainerStyle={styles.content}>
          <Text style={[styles.title, { fontFamily: typo.fontFamily.bold }]}>{t(setup.canSetUp ? 'setup.title' : 'onboarding.channels.title')}</Text>
          <Text style={styles.subtitle}>{t(setup.canSetUp ? 'setup.subtitle' : 'onboarding.channels.subtitle')}</Text>
          {totalSteps > 0 && (
            <View style={styles.progressBlock}>
              <View style={styles.progressTrack}>
                <View style={[styles.progressFill, { width: `${Math.max(4, Math.round(progressRatio * 100))}%` }]} />
              </View>
              <Text style={styles.progressText}>{t('setup.progress', { done: totalDone, total: totalSteps })}</Text>
            </View>
          )}
          {setup.canSetUp && <Text style={[styles.sectionTitle, { textAlign: isRTL ? 'right' : 'left' }]}>{t('setup.section.channels')}</Text>}

          {isLoading && profileCompletion.services.length === 0 ? (
            <ActivityIndicator size="large" color={colors.primary as string} style={styles.loader} />
          ) : (
            <View style={styles.list}>
              {channels.map((channel) => {
                const isConnected = connected.has(channel.serviceCode);
                return (
                  <TouchableOpacity
                    key={channel.serviceCode}
                    style={[styles.row, { flexDirection: isRTL ? 'row-reverse' : 'row' }, isConnected && styles.rowConnected]}
                    onPress={() => openChannel(channel)}
                    activeOpacity={0.8}
                  >
                    <View style={[styles.iconWrap, { backgroundColor: withAlpha(channel.color, 0.12) }]}>
                      <AppIcon name={channel.icon} size={24} color={channel.color} />
                    </View>
                    <View style={styles.rowBody}>
                      <Text style={[styles.rowTitle, { textAlign: isRTL ? 'right' : 'left' }]}>{t(channel.titleKey)}</Text>
                      <Text style={[styles.rowDescription, { textAlign: isRTL ? 'right' : 'left' }]}>{t(channel.descriptionKey)}</Text>
                    </View>
                    {renderStatus(isConnected, t('onboarding.channels.connected'), t('onboarding.channels.connect'))}
                  </TouchableOpacity>
                );
              })}
            </View>
          )}

          {businessSections.map(({ section, items }) => (
            <View key={section}>
              <Text style={[styles.sectionTitle, { textAlign: isRTL ? 'right' : 'left' }]}>{t(`setup.section.${section}` as any)}</Text>
              <View style={styles.list}>
                {items.map((item) => (
                  <TouchableOpacity
                    key={item.key}
                    style={[styles.row, { flexDirection: isRTL ? 'row-reverse' : 'row' }, item.done && styles.rowConnected]}
                    onPress={() => openStep(item)}
                    activeOpacity={0.8}
                    accessibilityRole="button"
                    accessibilityState={{ checked: item.done }}
                  >
                    <View style={[styles.iconWrap, { backgroundColor: withAlpha(colors.primary as string, 0.12) }]}>
                      <AppIcon name={item.icon} size={22} color={colors.primary as string} />
                    </View>
                    <View style={styles.rowBody}>
                      <Text style={[styles.rowTitle, { textAlign: isRTL ? 'right' : 'left' }]}>{t(`setup.step.${item.key}.title` as any)}</Text>
                      <Text style={[styles.rowDescription, { textAlign: isRTL ? 'right' : 'left' }]}>{t(`setup.step.${item.key}.description` as any)}</Text>
                    </View>
                    {renderStatus(item.done, t('setup.done'), t('setup.start'))}
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          ))}

          <Button
            title={connectedCount > 0 ? t('onboarding.channels.goToDashboard') : t('onboarding.channels.skipForNow')}
            onPress={() => navigateTo('home')}
            variant={connectedCount > 0 ? 'primary' : 'outline'}
            size="lg"
            fullWidth
            style={styles.finishButton}
          />
          <Text style={styles.hint}>{t('onboarding.channels.laterHint')}</Text>
        </ScrollView>
      </SafeAreaView>
    </DualPaneLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background.surface },
  content: { flexGrow: 1, paddingHorizontal: spacing.screenPadding, paddingTop: spacing.xl, paddingBottom: spacing.xl, maxWidth: 560, width: '100%', alignSelf: 'center' },
  loader: { marginVertical: spacing.xl },
  title: { fontSize: typography.fontSize['2xl'], color: colors.textDark, textAlign: 'center', marginBottom: spacing.sm },
  subtitle: { fontSize: typography.fontSize.sm, color: colors.textLight, textAlign: 'center', marginBottom: spacing.lg },
  list: { gap: spacing.sm },
  sectionTitle: { fontSize: typography.fontSize.xs, fontFamily: typography.fontFamily.semibold, color: colors.textLight, textTransform: 'uppercase', letterSpacing: 0.6, marginTop: spacing.lg, marginBottom: spacing.sm },
  progressBlock: { marginBottom: spacing.sm, gap: 6 },
  progressTrack: { height: 6, borderRadius: 3, backgroundColor: withAlpha(colors.primary as string, 0.15), overflow: 'hidden' },
  progressFill: { height: '100%', borderRadius: 3, backgroundColor: colors.primary },
  progressText: { fontSize: typography.fontSize.xs, color: colors.textLight, textAlign: 'center' },
  row: {
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
    borderRadius: spacing.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surfaceRaised,
  },
  rowConnected: { borderColor: withAlpha(colors.status.success, 0.45) },
  iconWrap: { width: 44, height: 44, borderRadius: 22, alignItems: 'center', justifyContent: 'center' },
  rowBody: { flex: 1, minWidth: 0 },
  rowTitle: { fontSize: typography.fontSize.base, fontFamily: typography.fontFamily.semibold, color: colors.textDark },
  rowDescription: { fontSize: typography.fontSize.xs, color: colors.textLight, marginTop: 2 },
  statusPill: { alignItems: 'center', gap: 4 },
  statusText: { fontSize: typography.fontSize.xs, color: colors.status.success, fontFamily: typography.fontFamily.semibold },
  connectText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  finishButton: { marginTop: spacing.xl },
  hint: { fontSize: typography.fontSize.xs, color: colors.textLight, textAlign: 'center', marginTop: spacing.sm },
});
